/**
 * Checkout Store - Internal
 * @see mobile: module-checkout/src/modules/checkout/stores/checkout.store.ts
 *
 * 100% matching mobile pattern - uses exact same store structure
 */

import { type Observable } from '@legendapp/state';
import { createPersistedStore } from '@kidslabs/module-core';
import type {
    CustomerAddress,
    Order,
    AvailableShippingMethod as BaseAvailableShippingMethod,
    ShippingCartAddress,
} from '../../../types/front.graphql';
import { type AvailableShippingMethod } from '../models/shipping-method.model';
import { type AvailablePaymentMethod } from '../models/payment-method.model';

/**
 * CheckoutStore type matching mobile exactly
 */
export type CheckoutStore = {
    selectedShippingAddress?: ShippingCartAddress;
    selectedBillingAddress?: CustomerAddress;
    selectedPaymentMethod?: AvailablePaymentMethod;
    selectedShippingMethod?: AvailableShippingMethod;
    customerNotes?: string;
    placedOrder?: Order;
    isUpdatingAddress?: boolean;
    lastPaymentMethodCode?: string;
    /**
     * Monotonic counter incremented whenever discount state changes
     * (coupon applied/removed, voucher applied/removed).
     * Hooks watch this to trigger their own refetch (FastCartTotals, shipping methods, etc.).
     */
    discountVersion?: number;
    /**
     * Monotonic counter incremented when SetShippingAddressesOnCart mutation returns
     * available_shipping_methods in its response. useShippingMethods watches this to
     * consume the pre-fetched data instead of making a redundant AvailableShippingMethods query.
     */
    addressShippingMethodsVersion?: number;
    /**
     * Available shipping methods returned by the SetShippingAddressesOnCart mutation.
     * Consumed by useShippingMethods to avoid a redundant round trip.
     */
    addressShippingMethodsData?: {
        available_shipping_methods: BaseAvailableShippingMethod[];
        selected_shipping_method?: {
            amount?: { currency?: string | null; value?: number | null };
            method_title?: string;
            method_code?: string;
            carrier_code: string;
            carrier_title?: string;
        };
    };
};

const initialState: CheckoutStore = {
    selectedShippingAddress: undefined,
    selectedBillingAddress: undefined,
    selectedPaymentMethod: undefined,
    selectedShippingMethod: undefined,
    customerNotes: '',
    placedOrder: undefined,
    isUpdatingAddress: false,
    lastPaymentMethodCode: undefined,
    discountVersion: 0,
    addressShippingMethodsVersion: 0,
    addressShippingMethodsData: undefined,
};

/**
 * Checkout observable store with automatic IndexedDB persistence
 *
 * ✅ Matches mobile structure exactly
 * ✅ Uses createPersistedStore for web compatibility
 */
export const checkoutStore$: Observable<CheckoutStore> = createPersistedStore('checkout', initialState);
